import React, { useState } from 'react';
import { Box, ClickAwayListener, Tooltip, Zoom } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import moment from 'moment';
import _ from 'lodash';
import { i18n } from '../../../../language';
import { onKeyUpEnterOrSpace, useGetUserInfo } from '../../../../utils';
import useMobile from '../../../../utils/useMobile';
import { RemarksBgIcon } from '../../../../components/Icon';

const useStylesLongremarkTooltip = makeStyles((theme) => ({
  arrow: {
    color: '#ffffff',
    '&::before': {
      boxShadow: '0 0 0.5rem 0 rgba(125, 134, 169, 0.2)',
    },
  },
  tooltip: {
    borderRadius: '0.625rem',
    boxShadow: '0 0 0.5rem 0 rgba(125, 134, 169, 0.2)',
    backgroundColor: '#ffffff',
    padding: '1.188rem 0.688rem 1.188rem 1.188rem',
    color: theme.palette.font.white,
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.875rem',
    fontWeight: '500',
    width: '100%',
    minWidth: '35rem',
    whiteSpace: 'pre-line',
  },
}));

const useStyles = makeStyles(() => ({
  activityBox: {
    display: 'flex',
    fontSize: '1rem',
    padding: '0.8rem 0.675rem',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  activtyTime: {
    color: '#538fff',
  },
  activtyCw: {
    color: '#2E4469',
  },
  itemBox: {
    display: 'flex',
  },
  itemTime: {
    color: '#2E4469',
    flexBasis: '40%',
  },
  itemDetail: {
    display: 'flex',
    gap: '0.5rem',
    flexBasis: '60%',
  },
  markiconBtn: {
    borderRadius: '0',
    padding: '0',
    marginLeft: '0.625rem',
    display: 'flex',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  markicon: {
    fontSize: '1.25rem',
    color: '#c0c6cb',
  },
}));

const historyTimeFormat = 'YYYY-MM-DD HH:mm:ss';

const HistoryLog = ({ transferHistoryLogList }) => {
  const [isPad] = useMobile();
  const longremarkclasses = useStylesLongremarkTooltip();
  const classes = useStyles();
  const [toolTipOpen, setToolTipOpen] = useState(false);

  const handleTooltipClose = (event) => {
    event.stopPropagation();
    setToolTipOpen(false);
  };

  const handleTooltipOpen = (event) => {
    event.stopPropagation();
    isPad && setToolTipOpen(true);
  };

  return isPad ? (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <Tooltip
        classes={longremarkclasses}
        title={<TransferHistory transferHistoryLogList={transferHistoryLogList} />}
        arrow
        TransitionComponent={Zoom}
        disableFocusListener
        disableHoverListener
        disableTouchListener
        onClose={handleTooltipClose}
        open={toolTipOpen}
      >
        <div
          className={classes.markiconBtn}
          onClick={handleTooltipOpen}
          tabIndex={0}
          onKeyUp={(e) => onKeyUpEnterOrSpace(e, handleTooltipOpen)}
        >
          <RemarksBgIcon className={classes.markicon} />
        </div>
      </Tooltip>
    </ClickAwayListener>
  ) : (
    <Tooltip
      classes={longremarkclasses}
      title={<TransferHistory transferHistoryLogList={transferHistoryLogList} />}
      arrow
      TransitionComponent={Zoom}
    >
      <div className={classes.markiconBtn}>
        <RemarksBgIcon className={classes.markicon} />
      </div>
    </Tooltip>
  );
};

export const TransferHistory = ({ transferHistoryLogList }) => {
  const classes = useStyles();
  const userInfo = useGetUserInfo();
  console.log('transferHistoryLogList', transferHistoryLogList);
  return (
    <Box className={classes.activityBox}>
      <Box className={classes.itemBox}>
        <Box className={classes.activtyTime} flexBasis="40%">
          {i18n.t('EQUIPMENT_ACTIVITY_TIME')}
        </Box>
        <Box className={classes.activtyTime} flexBasis="60%">
          {i18n.t('EQUIPMENT_ACTIVITY_DETAILS')}
        </Box>
      </Box>
      {_.orderBy(transferHistoryLogList, ['transferTime'], ['desc']).map((item) => (
        <React.Fragment key={item.transferTime}>
          {/* accepted transfer */}
          {item.acceptTransferTime && (
            <Box className={classes.itemBox}>
              <Box className={classes.itemTime}>{moment(item.acceptTransferTime).format(historyTimeFormat)}</Box>
              <Box className={classes.itemDetail}>
                <span className={classes.activtyTime}>{`${userInfo?.hospCode} - ${item.toWardName} ${
                  item.toLocation ? ` - ${item.toLocation}` : ''
                }`}</span>
                <span className={classes.activtyCw}>accepted transfer</span>
              </Box>
            </Box>
          )}
          {/* cancel transfer */}
          {item.cancelTransferTime && (
            <Box className={classes.itemBox}>
              <Box className={classes.itemTime}>{moment(item.cancelTransferTime).format(historyTimeFormat)}</Box>
              <Box className={classes.itemDetail}>
                <span className={classes.activtyTime}>{`${userInfo?.hospCode} - ${item.fromWardName} ${
                  item.fromLocation ? ` - ${item.fromLocation}` : ''
                }`}</span>
                <span className={classes.activtyCw}>Cancel transfer</span>
              </Box>
            </Box>
          )}
          {/* reject transfer */}
          {item.rejectTransferTime && (
            <Box className={classes.itemBox}>
              <Box className={classes.itemTime}>{moment(item.rejectTransferTime).format(historyTimeFormat)}</Box>
              <Box className={classes.itemDetail}>
                <span className={classes.activtyTime}>{`${userInfo?.hospCode} - ${item.toWardName} ${
                  item.toLocation ? ` - ${item.toLocation}` : ''
                }`}</span>
                <span className={classes.activtyCw}>Reject transfer</span>
              </Box>
            </Box>
          )}
          {/* transfer to */}
          {item.transferTime && (
            <Box className={classes.itemBox}>
              <Box className={classes.itemTime}>{moment(item.transferTime).format(historyTimeFormat)}</Box>
              <Box className={classes.itemDetail}>
                <span className={classes.activtyTime}>{`${userInfo?.hospCode} - ${item.fromWardName} ${
                  item.fromLocation ? ` - ${item.fromLocation}` : ''
                }`}</span>
                <span className={classes.activtyCw}>transfer to</span>
                <span className={classes.activtyTime}>{`${userInfo?.hospCode} - ${item.toWardName} ${
                  item.toLocation ? ` - ${item.toLocation}` : ''
                }`}</span>
              </Box>
            </Box>
          )}
        </React.Fragment>
      ))}
    </Box>
  );
};

export default HistoryLog;
